import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart, useSyncCartBusiness } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/soluqr/api/client';
import { withCustomerSession } from '@/lib/customerSession';
import { readLocalCustomer, isLocalCustomerComplete, saveLocalCustomer, saveCustomerSessionFromApi } from '@/lib/localCustomer';
import { normalizeCustomerPhone } from '@/lib/phone';
import { readOrderNotes, clearOrderNotes } from '@/lib/orderNotesStorage';
import { readCheckoutChatContext, clearCheckoutChatContext } from '@/lib/checkoutChatContext';
import { hasDeliveryZones } from '@/lib/deliveryZones';
import { uploadMenuOrderProof } from '@/lib/checkoutProofUpload';
import { submitCheckoutToWhatsApp } from '@/lib/checkoutSubmitOrder';
import {
  clearMpOrderReturnSession,
  loadMpOrderReturnSession,
} from '@/lib/checkout/mpReturnSession';
import { logStructured } from '@/lib/networkLog';
import { cn } from '@/lib/utils';
import CriticalPageErrorBoundary from '@/components/CriticalPageErrorBoundary';
import {
  getEnabledPaymentMethodsForCheckout,
  selectionToCanonicalPayment,
} from '@/lib/checkoutPaymentMethods';
import {
  formatBusinessDeliveryTime,
  formatBusinessPrepTime,
  openWhatsAppUrl,
} from '@/lib/checkoutWhatsAppOrder';
import { flushSync } from 'react-dom';
import {
  buildCheckoutIntroCopy,
  buildCustomerSectionIntro,
  buildNotesRecapIntro,
} from '@/lib/checkoutConversationalCopy';
import SoluQRBanner from '@/components/SoluQRBanner';
import { Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import CheckoutOrderPlacedDialog from './checkout/CheckoutOrderPlacedDialog';
import CheckoutPageHeader from './checkout/CheckoutPageHeader';
import CheckoutCartSummary from './checkout/CheckoutCartSummary';
import CheckoutCustomerSection from './checkout/CheckoutCustomerSection';
import CheckoutRewardsSection from './checkout/CheckoutRewardsSection';
import CheckoutServiceTypeSection from './checkout/CheckoutServiceTypeSection';
import CheckoutDeliverySection from './checkout/CheckoutDeliverySection';
import CheckoutTipSection from './checkout/CheckoutTipSection';
import CheckoutTimeHintsSection from './checkout/CheckoutTimeHintsSection';
import CheckoutPaymentSection from './checkout/CheckoutPaymentSection';
import CheckoutTotalsCard from './checkout/CheckoutTotalsCard';
import CheckoutNotesRecapCard from './checkout/CheckoutNotesRecapCard';
import CheckoutDemoPreviewCard from './checkout/CheckoutDemoPreviewCard';
import CheckoutDemoFooter from './checkout/CheckoutDemoFooter';
import { DEMO_DELIVERY_BY_COMMUNE } from './checkout/checkoutConstants';
import { useCheckoutDelivery } from './checkout/useCheckoutDelivery';
import { getEffectiveBusinessId } from '@/lib/tenant/getEffectiveBusinessId';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import {
  isDemoBusinessId,
  getDemoMenuPayload,
  DEMO_PUBLIC_BRAND,
  DEMO_CHECKOUT_DEFAULT_ADDRESS,
} from '@/lib/demo/demoBusiness';
import { trackCheckoutStarted, trackEvent } from '@/lib/analytics/trackEvent';
import {
  syncMenuTableContextFromSearchParams,
  getMenuTableCheckoutPatch,
} from '@/lib/menu/menuTableContext';
import {
  isPublicMenuCheckoutDisabled,
  MENU_CHECKOUT_BLOCKED_MESSAGE,
} from '@/lib/menuPlanCapabilities';

const CheckoutPageContent = () => {
  const { t, i18n } = useTranslation();
  const { businessId: routeBusinessId } = useParams();
  const routeBid = String(routeBusinessId ?? '').trim();
  const [businessId, setBusinessId] = useState(() => routeBid || null);
  const [businessIdReady, setBusinessIdReady] = useState(() => !!routeBid);

  useEffect(() => {
    if (routeBid) {
      setBusinessId(routeBid);
      setBusinessIdReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const id = await getEffectiveBusinessId('');
      if (!cancelled) {
        setBusinessId(id);
        setBusinessIdReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeBid]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cart, business, setBusiness, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  useSyncCartBusiness(businessId);

  const [serviceType, setServiceType] = useState('delivery');
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [address, setAddress] = useState('');
  const [markerPosition, setMarkerPosition] = useState(null);
  const [tip, setTip] = useState(0);
  const [paymentSelection, setPaymentSelection] = useState('efectivo');
  const [cashAmount, setCashAmount] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const sendingRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [checkoutReferralCode, setCheckoutReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralInfoLoading, setReferralInfoLoading] = useState(false);
  const [benefitsProgress, setBenefitsProgress] = useState(null);
  const [benefitsProgressLoading, setBenefitsProgressLoading] = useState(false);
  const [checkoutRewardChoice, setCheckoutRewardChoice] = useState('none');
  const [chatCtx, setChatCtx] = useState(null);
  const deliverySectionRef = useRef(null);

  const [orderPlacedOpen, setOrderPlacedOpen] = useState(false);
  const [orderPlacedWhatsappUrl, setOrderPlacedWhatsappUrl] = useState('');
  const [orderPlacedOrderId, setOrderPlacedOrderId] = useState(null);
  const [orderPlacedCopyText, setOrderPlacedCopyText] = useState('');
  const [orderPlacedWhatsappUnavailable, setOrderPlacedWhatsappUnavailable] = useState(false);
  const [orderPlacedTotal, setOrderPlacedTotal] = useState(null);
  const [orderPlacedTrackingUrl, setOrderPlacedTrackingUrl] = useState(null);
  const checkoutTrackingShownRef = useRef(false);

  const primaryColor = business?.primary_color || '#10b981';
  const isDemoCheckout = Boolean(business?.is_demo) || isDemoBusinessId(businessId);

  const useAddressZonesForDelivery = useMemo(
    () => hasDeliveryZones(business),
    [business?.delivery_zones]
  );

  const {
    deliveryQuote,
    deliveryLoading,
    geocodeLoading,
    solugoCoverage,
    solugoCoverageLoading,
    solugoDeliveryOutOfCoverage,
    solugoCoverageChecking,
    demoDeliveryMeta,
  } = useCheckoutDelivery({
    isDemoCheckout,
    useAddressZonesForDelivery,
    serviceType,
    businessId,
    business,
    address,
    markerPosition,
    setMarkerPosition,
    setAddress,
  });

  const mpReturnPending = Boolean(
    searchParams.get('payment') ||
      searchParams.get('mp_order') ||
      (businessId && loadMpOrderReturnSession(businessId)),
  );

  useEffect(() => {
    if (orderPlacedOpen) return;
    if (mpReturnPending) return;
    if (!businessId || !businessIdReady) return;
    if (cart.length === 0) {
      navigate(getPublicMenuPath(routeBid, 'home'));
    }
  }, [
    cart,
    businessId,
    businessIdReady,
    navigate,
    orderPlacedOpen,
    routeBid,
    mpReturnPending,
  ]);

  // Vuelta desde Mercado Pago: restaurar diálogo WhatsApp / copiar pedido
  useEffect(() => {
    if (!businessId || !businessIdReady) return;
    const payment = String(searchParams.get('payment') || '').toLowerCase();
    if (!payment && !searchParams.get('mp_order')) return;

    const saved = loadMpOrderReturnSession(businessId);
    if (payment === 'failure') {
      toast.error('El pago no se completó. Podés reintentar o elegir otro método.');
      clearMpOrderReturnSession(businessId);
    } else if (payment === 'pending') {
      toast.message('Pago pendiente de confirmación. Cuando se acredite, el local lo verá como pagado.');
    } else if (payment === 'success' || payment === 'approved') {
      toast.success('Pago recibido. Ahora podés avisar al local por WhatsApp.');
    }

    if (saved) {
      setOrderPlacedWhatsappUrl(saved.whatsappUrl || '');
      setOrderPlacedCopyText(saved.orderTextPlain || '');
      setOrderPlacedWhatsappUnavailable(Boolean(saved.whatsappUnavailable));
      setOrderPlacedTotal(typeof saved.totalSnap === 'number' ? saved.totalSnap : null);
      setOrderPlacedTrackingUrl(
        typeof saved.trackingUrl === 'string' && saved.trackingUrl.trim()
          ? saved.trackingUrl.trim()
          : null,
      );
      const showTracking =
        business?.order_tracking_enabled === true &&
        saved.orderId != null &&
        saved.orderId !== '';
      setOrderPlacedOrderId(showTracking ? saved.orderId : saved.orderId || null);
      setOrderPlacedOpen(true);
      if (payment === 'success' || payment === 'approved' || payment === 'pending') {
        clearMpOrderReturnSession(businessId);
      }
    }

    // Limpiar query para no reabrir el diálogo al refrescar
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    next.delete('mp_order');
    next.delete('mp_business');
    next.delete('collection_id');
    next.delete('collection_status');
    next.delete('payment_id');
    next.delete('status');
    next.delete('external_reference');
    next.delete('payment_type');
    next.delete('merchant_order_id');
    next.delete('preference_id');
    next.delete('site_id');
    next.delete('processing_mode');
    next.delete('merchant_account_id');
    setSearchParams(next, { replace: true });
  }, [businessId, businessIdReady, business?.order_tracking_enabled]);

  useEffect(() => {
    if (!businessId) return;
    logStructured('analytics', {
      event: 'checkout_opened',
      businessId,
      source: 'menu',
    });
    trackCheckoutStarted(businessId, { source: 'menu_checkout_page' });
  }, [businessId]);

  useEffect(() => {
    if (!orderPlacedOpen || !orderPlacedTrackingUrl) return;
    if (checkoutTrackingShownRef.current) return;
    checkoutTrackingShownRef.current = true;
    try {
      trackEvent('checkout_order_tracking_shown', {});
    } catch {
      /* noop */
    }
  }, [orderPlacedOpen, orderPlacedTrackingUrl]);

  useEffect(() => {
    if (!businessId) return;
    const c = readLocalCustomer(businessId);
    if (!isLocalCustomerComplete(c)) return;
    setCustomerName(c.name);
    setCustomerPhone(c.phone);
    setCustomerEmail(c.email || '');
    if (c.address) setAddress(String(c.address));
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    setOrderNotes(readOrderNotes(businessId));
    setChatCtx(readCheckoutChatContext(businessId));
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    const t = setTimeout(() => {
      const phone = normalizeCustomerPhone(customerPhone);
      if (!customerName.trim() || phone.length < 8) return;
      saveLocalCustomer(
        {
          name: customerName.trim(),
          phone,
          email: customerEmail.trim(),
          address: String(address || '').trim(),
        },
        businessId
      );
      if (!isDemoCheckout) {
        api
          .post('/customers/upsert', {
            business_id: businessId,
            phone,
            name: customerName.trim(),
            email: customerEmail.trim() || undefined,
            address: address ? String(address).trim() : undefined,
          })
          .then(({ data }) => saveCustomerSessionFromApi(businessId, data))
          .catch(() => {});
      }
    }, 700);
    return () => clearTimeout(t);
  }, [businessId, customerName, customerPhone, customerEmail, address, isDemoCheckout]);

  useEffect(() => {
    if (!businessId) return;
    syncMenuTableContextFromSearchParams(businessId, searchParams);
  }, [businessId, searchParams]);

  useEffect(() => {
    if (!businessId || !business) return;
    const tablePatch = getMenuTableCheckoutPatch(businessId, business);
    if (!tablePatch) return;
    setServiceType('mesa');
    setTableNumber(tablePatch.tableNumber);
  }, [businessId, business]);

  useEffect(() => {
    if (isDemoCheckout) return;
    const tablePatch = getMenuTableCheckoutPatch(businessId, business);
    if (tablePatch) return;
    if (business?.is_demo && business.service_types?.includes('retiro')) {
      setServiceType('retiro');
    }
  }, [business?.is_demo, business?.service_types, isDemoCheckout, businessId, business]);

  useEffect(() => {
    if (!isDemoCheckout) return;
    const tablePatch = getMenuTableCheckoutPatch(businessId, business);
    if (tablePatch) {
      setServiceType('mesa');
      setTableNumber(tablePatch.tableNumber);
      return;
    }
    if (serviceType !== 'delivery') setServiceType('delivery');
  }, [isDemoCheckout, businessId, business, serviceType]);

  useEffect(() => {
    if (!business?.service_types?.length) return;
    if (isDemoCheckout) return;
    const st = business.service_types;
    const pref = sessionStorage.getItem('soluqr_checkout_service_pref');
    if (pref === 'delivery' && st.includes('delivery')) {
      setServiceType('delivery');
      sessionStorage.removeItem('soluqr_checkout_service_pref');
    } else if (pref === 'retiro' && st.includes('retiro')) {
      setServiceType('retiro');
      sessionStorage.removeItem('soluqr_checkout_service_pref');
    }
  }, [business, isDemoCheckout]);

  useEffect(() => {
    const phone = normalizeCustomerPhone(customerPhone);
    if (!businessId || phone.length < 8) {
      setReferralInfo(null);
      setReferralInfoLoading(false);
      if (phone.length < 8) setCheckoutReferralCode('');
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setReferralInfoLoading(true);
      api
        .get(
          '/customers/referral-info',
          withCustomerSession({ params: { business_id: businessId, phone } }, businessId),
        )
        .then(({ data }) => {
          if (!cancelled) setReferralInfo(data);
        })
        .catch(() => {
          if (!cancelled) setReferralInfo(null);
        })
        .finally(() => {
          if (!cancelled) setReferralInfoLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [businessId, customerPhone]);

  useEffect(() => {
    setCheckoutRewardChoice('none');
  }, [businessId, customerPhone]);

  const { canonical: canonicalPaymentMethod, onlineManualKind: paymentOnlineManualKind } =
    useMemo(() => selectionToCanonicalPayment(paymentSelection), [paymentSelection]);

  useEffect(() => {
    if (paymentSelection !== 'efectivo') setCashAmount('');
  }, [paymentSelection]);

  const { methodIds: enabledPaymentMethods, usedFallback: paymentMethodsFallback } = useMemo(
    () => getEnabledPaymentMethodsForCheckout(business),
    [
      business?.accepts_cash,
      business?.accepts_transfer,
      business?.accepts_card,
      business?.accepts_mercadopago,
      business?.mercadopago_online,
    ]
  );

  useEffect(() => {
    if (!enabledPaymentMethods.length) return;
    if (!enabledPaymentMethods.includes(paymentSelection)) {
      setPaymentSelection(enabledPaymentMethods[0]);
    }
  }, [enabledPaymentMethods, paymentSelection]);

  useEffect(() => {
    const phone = normalizeCustomerPhone(customerPhone);
    if (!businessId || phone.length < 8) {
      setBenefitsProgress(null);
      setBenefitsProgressLoading(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setBenefitsProgressLoading(true);
      api
        .get(
          '/customers/benefits-progress',
          withCustomerSession({ params: { business_id: businessId, phone } }, businessId),
        )
        .then(({ data }) => {
          if (!cancelled) setBenefitsProgress(data);
        })
        .catch(() => {
          if (!cancelled) setBenefitsProgress(null);
        })
        .finally(() => {
          if (!cancelled) setBenefitsProgressLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [businessId, customerPhone]);

  useEffect(() => {
    if (!businessIdReady || !businessId || business) return;
    if (isDemoBusinessId(businessId)) {
      setBusiness(getDemoMenuPayload().business);
      return;
    }
    let cancelled = false;
    api
      .get(`/menu/${businessId}`)
      .then((r) => {
        if (!cancelled) setBusiness(r.data.business);
      })
      .catch((err) => {
        if (!cancelled) {
          logStructured('menu', {
            event: 'checkout_business_resolve_failed',
            businessId,
            status: err?.response?.status,
            code: err?.code,
          });
          toast.error('No se pudo cargar el negocio');
          navigate(getPublicMenuPath(routeBid, 'home'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, businessIdReady, business, setBusiness, navigate, routeBid]);

  const handleProofSelect = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setProofFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProofPreview(reader.result);
    };
    reader.onerror = () => {
      toast.error('No se pudo leer el archivo. Probá con otra imagen o PDF.');
      setProofFile(null);
      setProofPreview(null);
      setProofUrl(null);
    };
    reader.readAsDataURL(file);

    setUploadingProof(true);
    setProofUrl(null);
    try {
      const url = await uploadMenuOrderProof(file, {
        businessId,
        customer: {
          name: customerName,
          phone: customerPhone,
          email: customerEmail,
          address,
        },
      });
      setProofUrl(url);
      toast.success('Comprobante cargado');
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error(error?.message || 'Error al cargar comprobante');
      setProofFile(null);
      setProofPreview(null);
      setProofUrl(null);
    } finally {
      setUploadingProof(false);
      if (input) input.value = '';
    }
  };

  const copyBankAccount = () => {
    if (business?.bank_account_number) {
      navigator.clipboard.writeText(business.bank_account_number);
      toast.success('Número de cuenta copiado');
    }
  };

  const requestSendOrder = () => {
    if (sendingRef.current || loading) return;
    if (isDemoBusinessId(businessId) || business?.is_demo) {
      void handleSendOrder();
      return;
    }
    setConfirmSendOpen(true);
  };

  const handleSendOrder = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setConfirmSendOpen(false);
    setLoading(true);
    try {
      if (isDemoBusinessId(businessId) || business?.is_demo) {
        const sub = Number(getCartTotal()) || 0;
        const demoMeta = DEMO_DELIVERY_BY_COMMUNE.Providencia;
        const del = serviceType === 'delivery' ? demoMeta.fee : 0;
        const demoTotal = sub + del + (Number(tip) || 0);
        const entrega =
          serviceType === 'delivery' ? 'Delivery' : serviceType === 'mesa' ? 'Mesa' : 'Retiro en local';
        const addrLine =
          serviceType === 'delivery'
            ? String(address || '').trim() || DEMO_CHECKOUT_DEFAULT_ADDRESS
            : '';
        const notas = String(orderNotes || '').trim();
        const cartLines = cart.map(
          (item) =>
            `${item.quantity}× ${item.product?.name || 'Producto'} — $${(item.price * item.quantity).toLocaleString('es-CL')}`
        );
        const copyBody = [
          `${business?.name || 'Demo'} · ${DEMO_PUBLIC_BRAND.badge}`,
          '',
          'Resumen para el local (simulación)',
          ...cartLines,
          '',
          `Cliente: ${String(customerName || '').trim() || 'Cliente demo'}`,
          `Entrega: ${entrega}`,
          serviceType === 'delivery'
            ? `Tiempo estimado: ${demoMeta.eta} · Costo despacho: $${demoMeta.fee.toLocaleString('es-CL')}`
            : null,
          serviceType === 'delivery' && addrLine ? `Dirección: ${addrLine}` : null,
          `Total: $${demoTotal.toLocaleString('es-CL')}`,
          notas ? `Notas: ${notas}` : null,
          '',
          'Este mensaje no se envía por WhatsApp ni al backend.',
        ]
          .filter(Boolean)
          .join('\n');
        clearOrderNotes(businessId);
        clearCheckoutChatContext();
        setOrderPlacedWhatsappUrl('');
        setOrderPlacedCopyText(copyBody);
        setOrderPlacedWhatsappUnavailable(true);
        setOrderPlacedOrderId(null);
        setOrderPlacedTrackingUrl(null);
        setOrderPlacedTotal(demoTotal);
        setOrderPlacedOpen(true);
        clearCart();
        toast.success('Simulación lista');
        return;
      }

      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      }

      const result = await submitCheckoutToWhatsApp({
        businessId,
        business,
        cart,
        getCartTotal,
        customerName,
        customerPhone,
        customerEmail,
        serviceType,
        tableNumber,
        address,
        markerPosition,
        orderNotes,
        tip,
        paymentMethod: canonicalPaymentMethod,
        onlineManualKind: paymentOnlineManualKind,
        cashAmount,
        proofUrl,
        proofFile,
        deliveryQuote,
        deliveryLoading,
        useAddressZonesForDelivery,
        checkoutReferralCode,
        checkoutRewardChoice,
        referralInfo,
        benefitsProgress,
        idempotencyKey: idempotencyKeyRef.current,
        notifyError: (msg) => toast.error(msg),
        notifySuccess: (msg) => toast.success(msg),
      });

      if (!result.ok) {
        toast.error(
          result.error ||
            (result.duplicate
              ? 'Tu pedido ya se está enviando. No pulses otra vez.'
              : 'No se pudo registrar el pedido. Intenta nuevamente.'),
        );
        return;
      }

      idempotencyKeyRef.current = null;

      if (result.mercadopagoCheckoutUrl) {
        clearOrderNotes(businessId);
        clearCheckoutChatContext();
        clearCart();
        toast.success('Redirigiendo a Mercado Pago…');
        window.location.href = result.mercadopagoCheckoutUrl;
        return;
      }

      clearOrderNotes(businessId);
      clearCheckoutChatContext();
      setOrderPlacedWhatsappUrl(result.whatsappUrl || '');
      setOrderPlacedCopyText(result.orderTextPlain || '');
      setOrderPlacedWhatsappUnavailable(Boolean(result.whatsappUnavailable));
      setOrderPlacedTotal(typeof result.totalSnap === 'number' ? result.totalSnap : null);
      const tu =
        typeof result.trackingUrl === 'string' && result.trackingUrl.trim() !== ''
          ? result.trackingUrl.trim()
          : null;
      setOrderPlacedTrackingUrl(tu);
      const showTracking =
        business?.order_tracking_enabled === true && result.orderId != null && result.orderId !== '';
      setOrderPlacedOrderId(showTracking ? result.orderId : null);
      // Pintar el agradecimiento animado antes de saltar a WhatsApp.
      flushSync(() => {
        setOrderPlacedOpen(true);
      });
      clearCart();
      if (result.whatsappUrl) {
        openWhatsAppUrl(result.whatsappUrl, {
          businessId,
          source: 'menu_checkout_auto',
        });
        toast.success('¡Gracias! Abriendo WhatsApp con tu pedido');
      } else {
        toast.success('Pedido registrado correctamente');
      }
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const subtotal = Number(getCartTotal()) || 0;
  const deliveryLine =
    serviceType === 'delivery' && typeof deliveryQuote?.delivery_fee === 'number'
      ? deliveryQuote.delivery_fee
      : 0;
  const demoDeliveryLine =
    isDemoCheckout && serviceType === 'delivery' ? demoDeliveryMeta.fee : 0;
  const effectiveDeliveryLine = isDemoCheckout ? demoDeliveryLine : deliveryLine;
  const total = subtotal + effectiveDeliveryLine + (Number(tip) || 0);

  const serviceTypes = business?.service_types ?? [];
  const estimateDeliveryTxt = serviceTypes.includes('delivery')
    ? formatBusinessDeliveryTime(business)
    : null;
  const estimateRetiroTxt = serviceTypes.includes('retiro')
    ? formatBusinessPrepTime(business)
    : null;
  const estimateMesaTxt =
    serviceTypes.includes('mesa') &&
    business?.has_tables &&
    !serviceTypes.includes('retiro')
      ? formatBusinessPrepTime(business)
      : null;
  const showCheckoutTimeHints =
    business && (estimateDeliveryTxt || estimateRetiroTxt || estimateMesaTxt);

  const phoneOk = normalizeCustomerPhone(customerPhone).length >= 8;
  const clubPremioDisponible =
    benefitsProgress?.enabled && Number(benefitsProgress?.available_rewards) > 0;
  const clubPremioLabel =
    (benefitsProgress?.reward_text && String(benefitsProgress.reward_text).trim()) ||
    (benefitsProgress?.benefit_name && String(benefitsProgress.benefit_name).trim()) ||
    'Premio del club';
  const referralPremioDisponible =
    referralInfo?.customer_found && Number(referralInfo?.available_referral_rewards) > 0;
  const referralPremioLabel =
    (referralInfo?.reward_text && String(referralInfo.reward_text).trim()) || 'Premio por referido';
  const showCheckoutRewards =
    phoneOk &&
    (benefitsProgressLoading ||
      referralInfoLoading ||
      clubPremioDisponible ||
      referralPremioDisponible);

  const orderNotesTrim = String(orderNotes || '').trim();
  void i18n.language; // recalcular intros al cambiar idioma
  const introCopy = buildCheckoutIntroCopy({
    serviceType,
    businessName: business?.name,
    chatCtx,
    orderNotesTrim,
  });
  const hasName = !!customerName.trim();
  const customerIntro = buildCustomerSectionIntro({
    hasName,
    hasPhone: phoneOk,
    serviceType,
    needsAddress:
      serviceType === 'delivery' &&
      !isDemoCheckout &&
      !String(address || '').trim(),
    mood: chatCtx?.mood,
  });
  const scheduledDisplay = introCopy.scheduledHint || null;
  const notesRecapIntro = buildNotesRecapIntro({
    hasScheduled: !!scheduledDisplay,
    hasNotes: orderNotesTrim.length > 0,
    mood: chatCtx?.mood,
  });

  if (!businessIdReady && !routeBid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background px-6">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground text-center">{t('checkout.loadingCheckout')}</p>
      </div>
    );
  }

  if (businessIdReady && !businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <Card className="p-10 text-center max-w-md space-y-3">
          <h2 className="text-xl font-semibold">No pudimos encontrar este negocio</h2>
          <p className="text-sm text-muted-foreground">
            Abrilo desde el enlace del negocio o revisá que el dominio esté bien configurado.
          </p>
        </Card>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!isDemoCheckout && isPublicMenuCheckoutDisabled(business)) {
    return (
      <div className="min-h-screen bg-background">
        <CheckoutPageHeader routeBid={routeBid} isDemoCheckout={false} />
        <div className="max-w-md mx-auto px-4 py-12">
          <Card className="p-8 text-center space-y-4">
            <h1 className="text-xl font-semibold">{t('checkout.ordersUnavailable')}</h1>
            <p className="text-sm text-muted-foreground">{t('catalog.checkoutBlocked')}</p>
            <Button
              className="w-full"
              onClick={() => navigate(getPublicMenuPath(routeBid, 'catalog'))}
              data-testid="checkout-blocked-back-catalog"
            >
              {t('checkout.viewMenu')}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const demoServiceLabel =
    serviceType === 'delivery' ? 'Delivery' : serviceType === 'mesa' ? 'Mesa' : 'Retiro en local';
  const demoEffectiveAddress = String(address || '').trim() || DEMO_CHECKOUT_DEFAULT_ADDRESS;

  return (
    <div className={cn('min-h-screen bg-background pb-24')}>
      <CheckoutPageHeader routeBid={routeBid} isDemoCheckout={isDemoCheckout} />

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {isDemoCheckout ? (
          <Alert className="border-violet-200/50 bg-violet-500/[0.06] dark:border-violet-800/40 dark:bg-violet-950/20">
            <AlertTitle className="text-foreground">Vista previa</AlertTitle>
            <AlertDescription className="text-muted-foreground">
              Simulás el checkout completo: no se envía WhatsApp ni se guarda el pedido en el servidor.
            </AlertDescription>
          </Alert>
        ) : null}

        {isDemoCheckout ? (
          <CheckoutDemoPreviewCard
            serviceType={serviceType}
            demoDeliveryMeta={demoDeliveryMeta}
            customerName={customerName}
            demoServiceLabel={demoServiceLabel}
            demoEffectiveAddress={demoEffectiveAddress}
            total={total}
            orderNotesTrim={orderNotesTrim}
          />
        ) : null}

        <div className="rounded-xl border bg-muted/50 p-5 sm:p-6 space-y-2 shadow-sm">
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight text-foreground">{introCopy.headline}</h1>
          {introCopy.lines.map((line, i) => (
            <p key={i} className="text-muted-foreground text-sm sm:text-base">
              {line}
            </p>
          ))}
        </div>

        <CheckoutNotesRecapCard
          notesRecapIntro={notesRecapIntro}
          scheduledDisplay={scheduledDisplay}
          orderNotesTrim={orderNotesTrim}
        />

        <h2 className="text-xl font-semibold text-foreground/90">{t('checkout.yourOrder')}</h2>

        <CheckoutCartSummary
          cart={cart}
          removeFromCart={removeFromCart}
          updateQuantity={updateQuantity}
        />

        <CheckoutCustomerSection
          customerIntro={customerIntro}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerPhone={customerPhone}
          setCustomerPhone={setCustomerPhone}
          customerEmail={customerEmail}
          setCustomerEmail={setCustomerEmail}
          orderNotes={orderNotes}
          setOrderNotes={setOrderNotes}
          businessId={businessId}
          phoneOk={phoneOk}
          referralInfoLoading={referralInfoLoading}
          referralInfo={referralInfo}
          checkoutReferralCode={checkoutReferralCode}
          setCheckoutReferralCode={setCheckoutReferralCode}
        />

        {showCheckoutRewards ? (
          <CheckoutRewardsSection
            checkoutRewardChoice={checkoutRewardChoice}
            setCheckoutRewardChoice={setCheckoutRewardChoice}
            benefitsProgressLoading={benefitsProgressLoading}
            referralInfoLoading={referralInfoLoading}
            clubPremioDisponible={clubPremioDisponible}
            clubPremioLabel={clubPremioLabel}
            referralPremioDisponible={referralPremioDisponible}
            referralPremioLabel={referralPremioLabel}
          />
        ) : null}

        <CheckoutServiceTypeSection
          serviceType={serviceType}
          setServiceType={setServiceType}
          isDemoCheckout={isDemoCheckout}
          business={business}
          tableNumber={tableNumber}
          setTableNumber={setTableNumber}
        />

        {serviceType === 'delivery' ? (
          <CheckoutDeliverySection
            deliverySectionRef={deliverySectionRef}
            useAddressZonesForDelivery={useAddressZonesForDelivery}
            address={address}
            setAddress={setAddress}
            markerPosition={markerPosition}
            setMarkerPosition={setMarkerPosition}
            chatCtx={chatCtx}
            deliveryQuote={deliveryQuote}
            deliveryLoading={deliveryLoading}
            geocodeLoading={geocodeLoading}
            solugoCoverage={solugoCoverage}
            solugoCoverageLoading={solugoCoverageLoading}
            showSoluGoCoverageHint={Boolean(business?.solugo_fulfills_delivery)}
          />
        ) : null}

        <CheckoutTipSection tip={tip} setTip={setTip} primaryColor={primaryColor} />

        {showCheckoutTimeHints ? (
          <CheckoutTimeHintsSection
            estimateDeliveryTxt={estimateDeliveryTxt}
            estimateRetiroTxt={estimateRetiroTxt}
            estimateMesaTxt={estimateMesaTxt}
          />
        ) : null}

        <CheckoutPaymentSection
          paymentMethodsFallback={paymentMethodsFallback}
          paymentSelection={paymentSelection}
          setPaymentSelection={setPaymentSelection}
          enabledPaymentMethods={enabledPaymentMethods}
          cashAmount={cashAmount}
          setCashAmount={setCashAmount}
          business={business}
          total={total}
          proofPreview={proofPreview}
          proofUrl={proofUrl}
          uploadingProof={uploadingProof}
          onProofSelect={handleProofSelect}
          onClearProof={() => {
            setProofFile(null);
            setProofPreview(null);
            setProofUrl(null);
          }}
          onCopyBankAccount={copyBankAccount}
        />

        <CheckoutTotalsCard
          subtotal={subtotal}
          serviceType={serviceType}
          isDemoCheckout={isDemoCheckout}
          demoDeliveryLine={demoDeliveryLine}
          deliveryLoading={deliveryLoading}
          deliveryQuote={deliveryQuote}
          markerPosition={markerPosition}
          tip={tip}
          total={total}
          primaryColor={primaryColor}
        />

        {isDemoCheckout ? (
          <CheckoutDemoFooter routeBid={routeBid} primaryColor={primaryColor} />
        ) : null}

        <Button
          size="lg"
          className="w-full h-16 text-lg"
          onClick={requestSendOrder}
          disabled={
            isDemoCheckout
              ? loading
              : loading ||
                solugoDeliveryOutOfCoverage ||
                solugoCoverageChecking ||
                (serviceType === 'delivery' &&
                  (geocodeLoading ||
                    deliveryLoading ||
                    typeof deliveryQuote?.delivery_fee !== 'number'))
          }
          style={{ backgroundColor: primaryColor }}
        >
          {isDemoCheckout ? (
            <>{t('checkout.simulateOrder')}</>
          ) : loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('checkout.sending')}
            </>
          ) : paymentSelection === 'online_manual_mercadopago' &&
            business?.mercadopago_online === true ? (
            <>{t('checkout.mpOnlineLabel')}</>
          ) : (
            <>
              <Phone className="w-5 h-5 mr-2" /> {t('checkout.confirmWhatsApp')}
            </>
          )}
        </Button>
      </div>

      <AlertDialog open={confirmSendOpen} onOpenChange={(open) => !loading && setConfirmSendOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('checkout.confirmDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('checkout.confirmDialogBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>{t('checkout.cancel')}</AlertDialogCancel>
            {/* Button (no AlertDialogAction): Action + preventDefault rompe el envío async en mobile */}
            <Button
              type="button"
              disabled={loading}
              onClick={() => void handleSendOrder()}
              style={{ backgroundColor: primaryColor }}
              className="text-white"
            >
              {loading ? t('checkout.confirmDialogSending') : t('checkout.confirmDialogYes')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SoluQRBanner business={business} />

      <CheckoutOrderPlacedDialog
        isDemoCheckout={isDemoCheckout}
        orderPlacedOpen={orderPlacedOpen}
        onOrderPlacedOpenChange={(open) => {
          setOrderPlacedOpen(open);
          if (!open) {
            checkoutTrackingShownRef.current = false;
            setOrderPlacedWhatsappUrl('');
            setOrderPlacedCopyText('');
            setOrderPlacedWhatsappUnavailable(false);
            setOrderPlacedOrderId(null);
            setOrderPlacedTotal(null);
            setOrderPlacedTrackingUrl(null);
            navigate(getPublicMenuPath(routeBid, 'home'), { state: { orderThankYou: true } });
          }
        }}
        primaryColor={primaryColor}
        businessId={businessId}
        business={business}
        routeBid={routeBid}
        orderPlacedWhatsappUnavailable={orderPlacedWhatsappUnavailable}
        orderPlacedOrderId={orderPlacedOrderId}
        orderPlacedTotal={orderPlacedTotal}
        orderPlacedCopyText={orderPlacedCopyText}
        orderPlacedTrackingUrl={orderPlacedTrackingUrl}
        orderPlacedWhatsappUrl={orderPlacedWhatsappUrl}
      />
    </div>
  );
};

export default function CheckoutPage() {
  return (
    <CriticalPageErrorBoundary component="CheckoutPage">
      <CheckoutPageContent />
    </CriticalPageErrorBoundary>
  );
}
